import React from "react";
import { WhmStack } from "@securitize/reactjs-whm";
import { LoadingCard } from "./LoadingSkeletonCard";
import { useResolvedPath } from "react-router";
import { useGetInvestorInformationQuery } from "../../../data/useProfile";
import { InvestorVerificationNeeded } from "./content-restricted-pages/InvestorVerificationNeeded";
import { PersonalDetailsCard } from "./PersonalDetailsCard";
import { AddressCard } from "./AddressCard";
import { TaxJurisdictionCard } from "./TaxJurisdictionCard";
import { InvestorTypes } from "../../../data/types/investors";
import { ErrorPage } from "../../shared/ErrorPage";
import { EntityInformationForm } from "./entity/EntityInformationForm";
import {
  ProfilePermissionType,
  ProfilePermissionTypes,
} from "../../../data/dtos/profile-config.dto";
import { UserVerificationNeeded } from "./content-restricted-pages/UserVerificationNeeded";
import ContentRestricted from "./content-restricted-pages/ContentRestricted";
import { useCountries } from "../../shared/CountryTextField/CountriesContext";

export interface InformationProps {
  isPendingActionsLoading: boolean;
  permission: ProfilePermissionType;
}

export const LoadingSkeleton = () => (
  <>
    <LoadingCard />
    <LoadingCard />
    <LoadingCard />
  </>
);

export const Information = ({
  isPendingActionsLoading,
  permission,
}: InformationProps) => {
  const { pathname: currentPath } = useResolvedPath("");
  const countriesQuery = useCountries();
  const investorInfoQuery = useGetInvestorInformationQuery();
  const isEntity =
    investorInfoQuery.data?.investorType === InvestorTypes.ENTITY;

  if (
    investorInfoQuery.isPending ||
    isPendingActionsLoading ||
    countriesQuery.isLoading
  )
    return <LoadingSkeleton />;

  if (investorInfoQuery.error || countriesQuery.error) {
    const errorCode =
      investorInfoQuery.error?.correlationId ??
      countriesQuery.error?.correlationId;
    const refetchFailedCall = investorInfoQuery.error
      ? investorInfoQuery.refetch
      : countriesQuery.refetch;
    return (
      <ErrorPage
        errorCode={errorCode}
        onRefresh={() => {
          void refetchFailedCall();
        }}
      />
    );
  }

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_NOT_SUBMITTED) {
    return (
      <UserVerificationNeeded
        isVerificationInReview={false}
        currentPath={currentPath}
      />
    );
  }

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_PENDING) {
    return (
      <UserVerificationNeeded
        isVerificationInReview
        currentPath={currentPath}
      />
    );
  }

  if (permission === ProfilePermissionTypes.RESTRICTED) {
    return <ContentRestricted />;
  }

  if (
    permission === ProfilePermissionTypes.INVESTOR_VERIFICATION_NOT_SUBMITTED
  ) {
    return (
      <InvestorVerificationNeeded
        currentPath={currentPath}
        investorType={investorInfoQuery.data.investorType}
        entityName={investorInfoQuery.data.details.entityName}
      />
    );
  }

  if (permission === ProfilePermissionTypes.FULL_ACCESS) {
    return investorInfoQuery.isRefetching ? (
      <LoadingSkeleton />
    ) : (
      <EntityInformationForm investorInfoQuery={investorInfoQuery} kybPending />
    );
  }

  return (
    <>
      <WhmStack>
        {isEntity ? (
          <EntityInformationForm
            investorInfoQuery={investorInfoQuery}
            kybPending={false}
          />
        ) : (
          <WhmStack gap="20px" flex="1">
            <PersonalDetailsCard investorInfoQuery={investorInfoQuery} />
            <AddressCard investorInfoQuery={investorInfoQuery} />
            <TaxJurisdictionCard investorInfoQuery={investorInfoQuery} />
          </WhmStack>
        )}
      </WhmStack>
    </>
  );
};
